import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  FiPhone, FiGrid, FiUsers, FiCheckSquare, FiDollarSign, FiBookOpen, FiBarChart2,
  FiLogOut, FiSliders, FiX, FiSave, FiChevronDown, FiCoffee, FiCheckCircle, FiMoon,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { contactsAPI } from '../../services/api';
import { PhoneProvider, usePhoneCore } from '../../phone/PhoneContext';
import OrgBranchPicker from '../../components/contacts/OrgBranchPicker';
import PhoneIsland from './PhoneIsland';
import SettingsDrawer from './SettingsDrawer';
import {
  loadLook, saveLook, presetCss, getBgBlob, putBgBlob, deleteBgBlob, normalizeImage, effectiveFx,
} from './background';
import './op2.css';

// Havolalar NISBIY: panel ikkala manzilda ham ishlaydi (`/operator` va `/op`) —
// havolalar o'zi turgan manzilga moslanadi.
const NAV = [
  { to: '.', end: true, icon: FiGrid, label: 'Ish joyi' },
  { to: 'contacts', icon: FiUsers, label: 'Kontaktlar' },
  { to: 'tasks', icon: FiCheckSquare, label: 'Vazifalar' },
  { to: 'deals', icon: FiDollarSign, label: 'Bitimlar' },
  { to: 'knowledge', icon: FiBookOpen, label: 'Bilim bazasi' },
  { to: 'stats', icon: FiBarChart2, label: "Ko'rsatkichlarim" },
];

const STATUSES = [
  { key: 'online', label: 'Onlayn', hint: "Qo'ng'iroqlar sizga keladi", color: 'var(--g-ok)', icon: FiCheckCircle },
  { key: 'break', label: 'Tanaffus', hint: "Qo'ng'iroqlar yo'naltirilmaydi", color: 'var(--g-warn)', icon: FiCoffee },
  { key: 'offline', label: 'Oflayn', hint: 'Navbatdan chiqasiz', color: 'var(--g-tx3)', icon: FiMoon },
];

// Holat chipi — sarlavhada. Bosilsa uchta holat menyusi ochiladi.
function StatusChip() {
  const p = usePhoneCore();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const cur = STATUSES.find((s) => s.key === p.status) || STATUSES[2];

  return (
    <div className="op2-statuswrap" ref={boxRef}>
      <button className="op2-chip" onClick={() => setOpen(!open)} title="Holatni o'zgartirish">
        <span className="dot" style={{ background: cur.color }} />
        {cur.label}
        <FiChevronDown size={14} />
      </button>
      {open && (
        <div className="op2-menu g">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              onClick={() => { p.changeStatus(s.key); setOpen(false); }}
            >
              <s.icon size={15} color={s.color} />
              <span style={{ flex: 1 }}>
                {s.label}
                <small>{s.hint}</small>
              </span>
              {p.status === s.key && <FiCheckCircle size={14} color="var(--g-ok)" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Kontaktga saqlash oynasi. Tashkilot va filial ma'lumotnomadan tanlanadi.
function SaveContactModal({ phone, suggestedName, onClose, onSaved }) {
  const [form, setForm] = useState({ name: suggestedName || '', notes: '' });
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const name = form.name.trim();
    if (!name) { toast.error('Ism majburiy'); return; }
    setSaving(true);
    try {
      const res = await contactsAPI.create({
        phone, name, ...(org || {}), notes: form.notes.trim() || null,
      });
      toast.success('Kontakt saqlandi');
      onSaved(res.data?.contact || null, phone);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error;
      if (err.response?.status === 409) {
        toast.error(msg || 'Bu raqam allaqachon kontaktlarda bor');
        onSaved({ id: err.response?.data?.existing_id || null, name }, phone);
        onClose();
      } else {
        toast.error(msg || 'Kontaktni saqlashda xato');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="op2-modal-ov" onClick={() => !saving && onClose()}>
      <form className="op2-modal g" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <h3 style={{ flex: 1 }}>Kontaktga saqlash</h3>
          <button type="button" className="op2-iconbtn" onClick={onClose} disabled={saving}>
            <FiX size={17} />
          </button>
        </div>
        <div style={{ fontSize: 19, fontWeight: 800, fontFamily: 'monospace', margin: '12px 0 2px' }}>{phone}</div>

        <label className="op2-lab">Ism <span style={{ color: 'var(--g-bad)' }}>*</span></label>
        <input
          className="op2-in"
          autoFocus
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Mijoz ismi"
        />

        <OrgBranchPicker value={org} onChange={setOrg} />

        <label className="op2-lab">Izoh</label>
        <textarea
          className="op2-in"
          style={{ minHeight: 64, resize: 'vertical' }}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Ixtiyoriy"
        />

        <div style={{ display: 'flex', gap: 9, marginTop: 16 }}>
          <button type="button" className="op2-btn" style={{ flex: 1 }} onClick={onClose} disabled={saving}>
            Bekor qilish
          </button>
          <button type="submit" className="op2-btn go" style={{ flex: 1 }} disabled={saving || !form.name.trim()}>
            <FiSave size={15} /> {saving ? 'Saqlanmoqda…' : 'Saqlash'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Shell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const p = usePhoneCore();

  const [look, setLookState] = useState(() => loadLook(user?.id));
  const [bgUrl, setBgUrl] = useState(null);
  const [bgSize, setBgSize] = useState(0);
  const [bgBusy, setBgBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savePhone, setSavePhone] = useState(null);
  const urlRef = useRef(null);

  const setLook = useCallback((next) => {
    setLookState(next);
    saveLook(user?.id, next);
  }, [user?.id]);

  // Saqlangan fon rasmini (bo'lsa) IndexedDB'dan olib qo'yamiz.
  useEffect(() => {
    let alive = true;
    getBgBlob(user?.id).then((blob) => {
      if (!alive || !blob) return;
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setBgUrl(url);
      setBgSize(blob.size || 0);
    });
    return () => {
      alive = false;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [user?.id]);

  const uploadBg = useCallback(async (file) => {
    setBgBusy(true);
    try {
      const blob = await normalizeImage(file);
      await putBgBlob(user?.id, blob);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setBgUrl(url);
      setBgSize(blob.size || 0);
      setLook({ ...look, bg: 'custom' });
      toast.success('Fon rasmi qo\'yildi');
    } catch (e) {
      toast.error(e.message || "Rasmni yuklab bo'lmadi");
    } finally {
      setBgBusy(false);
    }
  }, [user?.id, look, setLook]);

  const removeBg = useCallback(async () => {
    await deleteBgBlob(user?.id);
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setBgUrl(null);
    setBgSize(0);
    if (look.bg === 'custom') setLook({ ...look, bg: 'preset:tun' });
    toast.success('Fon rasmi o\'chirildi');
  }, [user?.id, look, setLook]);

  // Enter — kiruvchi qo'ng'iroqqa javob. Maydonda yozayotganda ishlamaydi.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Enter' || e.ctrlKey || e.metaKey || e.altKey) return;
      if (p.callState !== 'incoming') return;
      const a = document.activeElement;
      if (a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) return;
      e.preventDefault();
      p.answer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [p.callState, p.answer]);

  const openSaveContact = useCallback((phone) => {
    const num = phone || p.peerNumber;
    if (num) setSavePhone(num);
  }, [p.peerNumber]);

  // Barqaror obyekt: har chizishda yangisi berilsa ish joyi ham qayta chizilardi.
  const outletCtx = useMemo(() => ({ openSaveContact }), [openSaveContact]);

  const onContactSaved = (contact, phone) => {
    if (contact && phone === p.peerNumber) {
      if (contact.name) p.setContactName(contact.name);
      if (contact.id) p.setContactId(contact.id);
    }
    if (phone) p.loadPeerCard(phone);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const useCustom = look.bg === 'custom' && !!bgUrl;
  const bgStyle = useCustom
    ? { backgroundImage: `url(${bgUrl})` }
    : { background: presetCss(String(look.bg || '').replace('preset:', '')) };

  return (
    <div
      className={`op2${look.theme === 'light' ? ' light' : ''}${effectiveFx(look) === 'lite' ? ' lite' : ''}`}
      style={{ '--scrim': look.scrim }}
    >
      <div className="op2-bg" style={bgStyle} />
      <div className="op2-scrim" />

      <div className="op2-shell">
        <header className="op2-top g">
          <div className="op2-brand">
            <div className="op2-brand-ic"><FiPhone size={15} /></div>
            <span className="op2-brand-tx">AUD-IT SOFT</span>
          </div>

          <nav className="op2-tabs">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? 'active' : '')}>
                <n.icon size={15} />
                <span>{n.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="op2-top-right">
            <div className="op2-me">
              <div className="op2-me-n">{user?.full_name}</div>
              <div className="op2-me-e">{user?.sip_extension ? `#${user.sip_extension}` : "ichki raqam yo'q"}</div>
            </div>
            {user?.sip_extension ? <StatusChip /> : null}
            <button
              className="op2-iconbtn"
              onClick={() => setSettingsOpen(true)}
              title="Sozlamalar — fon rasmi, ko'rinish, ovoz"
            >
              <FiSliders size={17} />
            </button>
            <button className="op2-iconbtn" onClick={handleLogout} title="Chiqish">
              <FiLogOut size={16} />
            </button>
          </div>
        </header>

        {!user?.sip_extension && (
          <div className="op2-peer g" style={{ padding: 16 }}>
            <div style={{ fontSize: 13.5, color: 'var(--g-tx2)', lineHeight: 1.55 }}>
              Bu hisobga ichki raqam biriktirilmagan, shuning uchun telefon ishlamaydi —
              panelning qolgan qismini ko'rish mumkin.
            </div>
          </div>
        )}

        <Outlet context={outletCtx} />
      </div>

      {user?.sip_extension ? <PhoneIsland onSaveContact={() => openSaveContact()} /> : null}

      {settingsOpen && (
        <SettingsDrawer
          look={look}
          setLook={setLook}
          bgUrl={bgUrl}
          bgSize={bgSize}
          busy={bgBusy}
          onUpload={uploadBg}
          onRemove={removeBg}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {savePhone && (
        <SaveContactModal
          phone={savePhone}
          suggestedName={savePhone === p.peerNumber ? p.contactName : ''}
          onClose={() => setSavePhone(null)}
          onSaved={onContactSaved}
        />
      )}
    </div>
  );
}

export default function Operator2Layout() {
  return (
    <PhoneProvider>
      <Shell />
    </PhoneProvider>
  );
}
